/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.29.0
 * @since 1.14.0
 */

import { useState } from 'react';

import { useSelector } from 'react-redux';

import { Button, EllipseText, LinkBlock } from '@components/common';
import useTableFooterButtons from '@components/common/TableFooter/hooks/useTableFooterButtons';
import { LabelEditorColorStyled } from '@components/Table/components/footerModal/LabelEditor/components/styled/LabelEditorListStyled';
import { ILabelColor } from '@components/Table/constants/labelColor';
import { TableFooterNameType } from '@components/Table/types/tableTypes';
import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';
import { useSubscriberIdContext } from '@pages/PageSubscriberId/context';

import { useCheckCustomRole } from '../../../../../utils/useCheckCustomRole';
import { manageNumberTranslationSelector } from '../selectors';
import { useGetManageNumberMetaQuery } from '../store';
import { ManageNumberLabelsStyled } from './styled/ManageNumberStyled';

const ManageNumberLabels = () => {
    const { subscriberId } = useSubscriberIdContext();

    const translationManageNumber = useSelector(manageNumberTranslationSelector());
    const isCustomRoleCheckAvailable = useCheckCustomRole(true, AuthorityRoleEnum.ROLE_TAGS_IMPORT);

    const [isFullLabelShow, setFullLabelsShow] = useState(false);

    const { labels } = useGetManageNumberMetaQuery(subscriberId, {
        selectFromResult: ({ data }) => ({
            labels: data?.labels ?? [],
        }),
    });

    const { labelEdit } = useTableFooterButtons(TableFooterNameType.subscriberId);

    return (
        <ManageNumberLabelsStyled>
            <div className="subscriber-id__title subscriber-id__title_bold">{translationManageNumber.labels.title}</div>
            {labels.length === 0 && (
                <div className="manage-number__label-items-empty">{translationManageNumber.labels.empty}</div>
            )}
            {(isFullLabelShow ? labels : labels.slice(0, 5)).map((label) => (
                <div className="manage-number__label-item" key={label.id}>
                    <LabelEditorColorStyled
                        className="manage-number__label-color"
                        color={('color' + label.color) as ILabelColor}
                    />
                    <EllipseText title={label.name}>{label.name}</EllipseText>
                </div>
            ))}
            {labels.length > 5 && (
                <LinkBlock className="manage-number__show-all" onClick={() => setFullLabelsShow(!isFullLabelShow)}>
                    {isFullLabelShow
                        ? translationManageNumber.labels.showShort
                        : translationManageNumber.labels.showAll}
                </LinkBlock>
            )}
            {isCustomRoleCheckAvailable && (
                <Button
                className="manage-number__btn"
                dataName="manage-number-edit-labels"
                kind="mediumNormal"
                onClick={labelEdit.onClick}
                >
                    {translationManageNumber.labels.change}
                </Button>)}
        </ManageNumberLabelsStyled>
    );
};

export default ManageNumberLabels;
